<template>
  <template v-if="hasSkeletons">
    <UpmSkeletonWrapper class="flex flex-col" :active="isFetching">
      <UpmSkeleton class="mt-4 h-8 w-32" />
    </UpmSkeletonWrapper>
  </template>

  <template v-else>
    <div class="">
      <!-- REGULAR PRICE -->
      <template v-if="!!product?.hasDiscount">
        <p
          class="text-danger dark:text-danger flex items-center gap-x-1 text-sm font-medium line-through"
        >
          <TagIcon class="h-5 w-5" />
          <em>{{
            $t("plan_card.was_price", {
              price: showLMFP
                ? product?.regularMonthlyFromPrice
                : product?.regularPrice,
            })
          }}</em>
        </p>
      </template>

      <!-- CURRENT PRICE INTRO -->

      <template v-else>
        <p class="text-auto-50 text-sm">
          {{
            product?.hasBillingCycle
              ? $t("plan_card.from")
              : $t("plan_card.only")
          }}
        </p>
      </template>

      <!-- CURRENT PRICE -->

      <p class="flex items-baseline gap-x-1">
        <span class="text-auto text-4xl font-bold tracking-tight">
          <slot name="price">{{ computedPrice }}</slot>
        </span>

        <!-- CURRENT PRICE SUFFIX -->

        <template v-if="computedPriceSuffix">
          <span class="text-auto-50 leading-6">{{ computedPriceSuffix }}</span>
        </template>
      </p>
    </div>
  </template>
</template>

<script setup lang="ts">
import UpmSkeleton from "@src/components/UpmSkeleton.vue";
import UpmSkeletonWrapper from "@src/components/UpmSkeletonWrapper.vue";
import _isEmpty from "lodash/isEmpty";
import _pick from "lodash/pick";
import { TagIcon } from "@heroicons/vue/20/solid";
import { computed, inject, ref } from "vue";
import { useCatalogue } from "@src/composables/catalogue";
import { useI18n } from "@src/composables/i18n";

const { $t, $tc } = useI18n({ packs: ["plan_card", "core"] });

const props = withDefaults(
  defineProps<{
    coupons?: string[];
    currencyCode?: string;
    id?: string;
    price?: string;
    priceSuffix?: string;
    showLMFP?: boolean; // Show [L]owest [M]onthly [F]rom [P]rice
  }>(),
  {
    coupons: () => [],
    currencyCode: "",
    id: "",
    price: "",
    priceSuffix: "",
    showLMFP: true,
  }
);

const { getProduct, setCurrencyFilter, setCouponsFilter } = useCatalogue({
  locale: ref(inject("locale", "en")),
  currencyCode: props.currencyCode,
  showLMFP: props.showLMFP,
});

const {
  data: product,
  isError,
  isFetching,
} = getProduct(props.id, [
  setCurrencyFilter(props.currencyCode),
  setCouponsFilter(props.coupons),
]);

const hasSkeletons = computed(() => !!isError.value || _isEmpty(product.value));

const computedPrice = computed(() => {
  if (props.price) return props.price;
  return props.showLMFP
    ? product?.value?.currentMonthlyFromPrice
    : product?.value?.currentPrice;
});

const computedPriceSuffix = computed(() => {
  if (props.priceSuffix) return props.priceSuffix;
  if (props.showLMFP && product?.value?.hasBillingCycle)
    return ["/", $tc("core.n_months", 1)].join(" ");
  return;
});
</script>
